import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const C = {
  bg: '#0F0F0F',
  surf: '#161616',
  card: '#1C1C1C',
  bdr: '#2A2A2A',
  accent: '#D97757',
  green: '#4CAF82',
  text: '#F0EDE8',
  muted: '#888',
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.bg}; color: ${C.text}; font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
  @keyframes spin { to { transform: rotate(360deg) } }
  input { color-scheme: dark; font-family: 'Inter', sans-serif; }
`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Something went wrong.');
      }
      setSent(true);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const inp = {
    width: '100%',
    padding: '12px 14px',
    background: C.surf,
    border: `0.5px solid ${C.bdr}`,
    borderRadius: 8,
    color: C.text,
    fontSize: 15,
    outline: 'none',
    transition: 'border-color .15s',
  };

  return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 400, animation: 'fadeUp .4s ease' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 36, justifyContent: 'center' }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' }}>C</div>
            <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.5px' }}>
              Content<span style={{ color: C.accent }}>Gap</span>
            </span>
          </div>

          <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 12, padding: '28px 26px' }}>
            {sent ? (
              <div style={{ textAlign: 'center' }}>
                <CheckCircle2 size={42} color={C.green} strokeWidth={1.6} style={{ marginBottom: 14 }} />
                <h1 style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-.5px', marginBottom: 8 }} data-testid="forgot-success-heading">Check your inbox</h1>
                <p style={{ fontSize: 15, color: C.muted, marginBottom: 6, lineHeight: 1.6 }}>
                  If an account with <strong style={{ color: C.text }}>{email}</strong> exists, we've sent a password reset link.
                </p>
                <p style={{ fontSize: 13, color: C.muted, marginBottom: 22, lineHeight: 1.5 }}>
                  The link is valid for <strong>1 hour</strong>. Don't see the email? Check your spam folder.
                </p>
                <Link
                  to="/login"
                  data-testid="back-to-signin-link"
                  style={{ display: 'inline-block', padding: '10px 22px', background: 'transparent', color: C.text, border: `0.5px solid ${C.bdr}`, borderRadius: 8, fontSize: 14, textDecoration: 'none' }}
                >
                  Back to sign in
                </Link>
              </div>
            ) : (
              <>
                <h1 style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-.5px', marginBottom: 6 }} data-testid="forgot-heading">Forgot your password?</h1>
                <p style={{ fontSize: 15, color: C.muted, marginBottom: 22, lineHeight: 1.6 }}>
                  Enter your email and we'll send you a link to reset your password.
                </p>
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, color: C.muted, marginBottom: 5 }}>Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      style={inp}
                      data-testid="forgot-email-input"
                      autoFocus
                      onFocus={(e) => (e.target.style.borderColor = C.accent)}
                      onBlur={(e) => (e.target.style.borderColor = C.bdr)}
                    />
                  </div>

                  {error && (
                    <div data-testid="forgot-error" style={{ background: 'rgba(220,60,60,0.10)', border: '0.5px solid rgba(220,60,60,0.3)', borderRadius: 7, padding: '9px 13px', fontSize: 13, color: '#FF7070', marginBottom: 14 }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: C.accent,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 15,
                      fontWeight: 500,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    {loading ? 'Sending...' : (<><Mail size={15} strokeWidth={1.8} /> Send reset link</>)}
                  </button>
                </form>
              </>
            )}
          </div>

          <p style={{ textAlign: 'center', marginTop: 18, fontSize: 14, color: C.muted }}>
            Remember your password?{' '}
            <Link to="/login" data-testid="back-to-login-link" style={{ color: C.accent, fontWeight: 500, textDecoration: 'none' }}>
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
